import React, { useState, useEffect, useMemo } from 'react';
import './submission.css'; 
import ActorCard from '../../components/actors/ActorCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faTasks, faUndo } from '@fortawesome/free-solid-svg-icons';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import {
  UpdateInvitationStatus,
  UpdateApplicationStatus,
  fetchAllInvitation,
  fetchALlApplications,
} from '../../api/axios';
import Alert from '../../components/common/Alert';
import { faMessage } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const Submission = ({ castingId, roleId, castingTitle }) => {
  const [activeTab, setActiveTab] = useState('Applied');
  const [allInvitations, setAllInvitations] = useState([]);
  const [allApplications, setAllApplications] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (castingId && roleId) {
      fetchAllSubmissions();
    }
  }, [castingId, roleId]);

  useEffect(() => {
    if (castingId && roleId) {
      setActiveTab('Applied');
    }
  }, [castingId, roleId]);

  const fetchAllSubmissions = async () => {
    setLoading(true);
    try {
      const [invResponse, appResponse] = await Promise.all([
        fetchAllInvitation(castingId, roleId),
        fetchALlApplications(castingId, roleId),
      ]);
      setAllInvitations(invResponse?.invitations || []);
      setAllApplications(appResponse?.applications || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setAllInvitations([]);
      setAllApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let invitations = [];
    let applications = [];

    if (activeTab === 'Invited') {
      invitations = allInvitations.filter(
        (invite) => invite.inviteStatus === 'pending'
      );
    } else if (activeTab === 'Applied') {
      invitations = allInvitations.filter(
        (invite) => invite.inviteStatus === 'approve'
      );
      applications = allApplications.filter(
        (app) => app.castingApproval === 'pending'
      );
    } else if (activeTab === 'Shortlist') {
      invitations = allInvitations.filter(
        (invite) => invite.inviteStatus === 'shortlist'
      );
      applications = allApplications.filter(
        (app) => app.castingApproval === 'shortlist'
      );
    } else if (activeTab === 'Declined') {
      invitations = allInvitations.filter(
        (invite) => invite.inviteStatus === 'reject'
      );
      applications = allApplications.filter(
        (app) => app.castingApproval === 'reject'
      );
    }

    return { invitations, applications };
  }, [activeTab, allInvitations, allApplications]);

  const { invitations, applications } = filteredData;

  const ChangeInvitationStatus = async (invitationId, inviteStatus) => {
    try {
      const res = await UpdateInvitationStatus(invitationId, inviteStatus);
      if (res.success) {
        // Update local state for real-time UI update
        setAllInvitations((prev) =>
          prev.map((inv) =>
            inv.id === invitationId ? { ...inv, inviteStatus } : inv
          )
        );
        setOpenSnackbar(true);
        setAlertSeverity('success');
        setAlertMessage(`Update Invitation status to ${inviteStatus}`);
      } else {
        setOpenSnackbar(true);
        setAlertSeverity('error');
        setAlertMessage('An error occurred while updating invitation status.');
      }
    } catch (error) {
      console.error('Error in updating invitation status.', error);
      setOpenSnackbar(true);
      setAlertSeverity('error');
      setAlertMessage('An error occurred while updating invitation status.');
    }
  };

  const ChangeApplicationStatus = async (applicationId, applicationStatus) => {
    try {
      const res = await UpdateApplicationStatus(
        applicationId,
        applicationStatus
      );
      if (res.success) {
        // Update local state for real-time UI update
        setAllApplications((prev) =>
          prev.map((app) =>
            app.id === applicationId ? { ...app, castingApproval: applicationStatus } : app
          )
        );
        setOpenSnackbar(true);
        setAlertSeverity('success');
        setAlertMessage(`Update application status to ${applicationStatus}`);
      } else {
        setOpenSnackbar(true);
        setAlertSeverity('error');
        setAlertMessage('An error occurred while updating application status.');
      }
    } catch (error) {
      console.error('Error in updating application status.', error);
      setOpenSnackbar(true);
      setAlertSeverity('error');
      setAlertMessage('An error occurred while updating application status.');
    }
  };

  const RevertInvitationStatus = (invitation) => {
    // Logic: If there's an application for this user, they "Applied", so revert to "approve"
    // Otherwise, they were just "Invited", so revert to "pending"
    const hasApplication = allApplications.some(
      (app) => app.userId === invitation.userId
    );
    const targetStatus = hasApplication ? 'approve' : 'pending';
    ChangeInvitationStatus(invitation.id, targetStatus);
  };

  const StyledBadge = styled(Badge)({
    '& .MuiBadge-badge': {
      backgroundColor: 'red',
      color: 'white',
      fontSize: '12px',
      padding: '6px 10px',
      borderRadius: '8px',
      minWidth: 'auto',
    },
  });

    const handleMessage = (recipient) => {
    navigate('/messages', {
      state: {
        castingId,
        recipientId: recipient.id,
        recipient,
        castingTitle,
      },
    });
  };


  // console.log('Invitations:', invitations, applications);


  return (
    <div className='submission-container'>
      <div className='content-header'>
        <h1>Casting Call Title: {castingTitle}</h1>
      </div>
      <div className='status-tabs'>
        {['Invited', 'Applied', 'Shortlist', 'Declined'].map((tab) => (
          <span
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </span>
        ))}
      </div>

      <div className='content'>
        {activeTab === 'Invited' && (
          <div className='sub-content'>
            {loading ? (
              <p>Loading...</p>
            ) : (
              invitations.length === 0 && (
                <>
                  <p>No Invitations found.</p> <hr />
                </>
              )
            )}
            <h4>Invitations</h4>
            <div className='actors-list'>
              {invitations.map((invitation) => (
                <div className='actor-card-layout' key={invitation.id}>
                  <ActorCard
                    actor={{
                      user: invitation.invitedUser,
                      profile: invitation.invitedUser.profile || null,
                    }}
                    ratings={invitation.invitedUser.rate || 0}
                    userRole={invitation.invitedUser.role}
                    role={invitation.castingRole}
                    isFromSubmission={true}
                  />
                  <div className='invite-btn-group'>
                    <Tooltip title='Decline'>
                      <IconButton
                        onClick={() =>
                          ChangeInvitationStatus(invitation.id, 'reject')
                        }
                      >
                        <FontAwesomeIcon
                          className='Icon-buttons'
                          icon={faBan}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Shortlist'>
                      <IconButton
                        onClick={() =>
                          ChangeInvitationStatus(invitation.id, 'shortlist')
                        }
                      >
                        <FontAwesomeIcon
                          className='Icon-buttons'
                          icon={faTasks}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Message'>
                      <IconButton
                        onClick={() => handleMessage(invitation.invitedUser)}
                      >
                        <FontAwesomeIcon
                          className='Icon-buttons'
                          icon={faMessage}
                        />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'Applied' && (
          <>
            <div className='sub-content'>
              {loading ? (
                <p>Loading...</p>
              ) : (
                invitations.length === 0 &&
                applications.length === 0 && (
                  <>
                    <p>No applicants found for this role.</p> <hr />
                  </>
                )
              )}
              {invitations.length > 0 && (
                <>
                  <h4>Accepted Invitations</h4>
                  <div className='actors-list'>
                    {invitations.map((invitation) => (
                      <div className='actor-card-layout' key={invitation.id}>
                        <ActorCard
                          actor={{
                            user: invitation.invitedUser,
                            profile: invitation.invitedUser.profile || null,
                          }}
                          ratings={invitation.invitedUser.rate || 0}
                          userRole={invitation.invitedUser.role}
                          role={invitation.castingRole}
                          isFromSubmission={true}
                        />
                        <div className='invite-btn-group'>
                          <Tooltip title='Decline'>
                            <IconButton
                              onClick={() =>
                                ChangeInvitationStatus(invitation.id, 'reject')
                              }
                            >
                              <FontAwesomeIcon
                                className='Icon-buttons'
                                icon={faBan}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Shortlist'>
                            <IconButton
                              color='success'
                              onClick={() =>
                                ChangeInvitationStatus(
                                  invitation.id,
                                  'shortlist'
                                )
                              }
                            >
                              <FontAwesomeIcon
                                className='Icon-buttons'
                                icon={faTasks}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Message'>
                            <IconButton
                              onClick={() =>
                                handleMessage(invitation.invitedUser)
                              }
                            >
                              <FontAwesomeIcon
                                className='Icon-buttons'
                                icon={faMessage}
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                  <hr />
                </>
              )}

              <h4>Direct Applications</h4>
              <div className='actors-list'>
                {applications.map((application) => (
                  <div className='actor-card-layout' key={application.id}>
                    <ActorCard
                      actor={{
                        user: application.invitedUser,
                        profile: application.invitedUser.profile || null,
                      }}
                      ratings={application.invitedUser.rate || 0}
                      userRole={application.invitedUser.role}
                      role={application.castingRole}
                      isFromSubmission={true}
                    />
                    <div className='invite-btn-group'>
                      <Tooltip title='Reject'>
                        <IconButton
                          onClick={() =>
                            ChangeApplicationStatus(application.id, 'reject')
                          }
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faBan}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Shortlist'>
                        <IconButton
                          color='success'
                          onClick={() =>
                            ChangeApplicationStatus(application.id, 'shortlist')
                          }
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faTasks}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Message'>
                        <IconButton
                          onClick={() => handleMessage(application.invitedUser)}
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faMessage}
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'Shortlist' && (
          <>
            {loading ? (
              <p>Loading...</p>
            ) : (
              invitations.length === 0 &&
              applications.length === 0 && (
                <>
                  <p>No Shortlists found.</p> <hr />
                </>
              )
            )}
            <div className='sub-content'>
              <h4>Shortlist</h4>
              <div className='actors-list'>
                {invitations.map((invitation) => (
                  <div className='actor-card-layout' key={invitation.id}>
                    <ActorCard
                      actor={{
                        user: invitation.invitedUser,
                        profile: invitation.invitedUser.profile || null,
                      }}
                      ratings={invitation.invitedUser.rate || 0}
                      userRole={invitation.invitedUser.role}
                      role={invitation.castingRole}
                      isFromSubmission={true}
                    />
                    <div className='invite-btn-group'>
                      <Tooltip title='Move back to previous status'>
                        <IconButton
                          onClick={() => RevertInvitationStatus(invitation)}
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faUndo}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Reject'>
                        <IconButton
                          onClick={() =>
                            ChangeInvitationStatus(invitation.id, 'reject')
                          }
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faBan}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Message'>
                        <IconButton
                          onClick={() => handleMessage(invitation.invitedUser)}
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faMessage}
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                ))}
                {applications.map((application) => (
                  <div className='actor-card-layout' key={application.id}>
                    <ActorCard
                      actor={{
                        user: application.invitedUser,
                        profile: application.invitedUser.profile || null,
                      }}
                      ratings={application.invitedUser.rate || 0}
                      userRole={application.invitedUser.role}
                      role={application.castingRole}
                      isFromSubmission={true}
                    />
                    <div className='invite-btn-group'>
                      <Tooltip title='Move back to Applied'>
                        <IconButton
                          onClick={() =>
                            ChangeApplicationStatus(application.id, 'pending')
                          }
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faUndo}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Reject'>
                        <IconButton
                          onClick={() =>
                            ChangeApplicationStatus(application.id, 'reject')
                          }
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faBan}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Message'>
                        <IconButton
                          onClick={() => handleMessage(application.invitedUser)}
                        >
                          <FontAwesomeIcon
                            className='Icon-buttons'
                            icon={faMessage}
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'Declined' && (
          <div className='sub-content'>
            {loading ? (
              <p>Loading...</p>
            ) : (
              invitations.length === 0 &&
              applications.length === 0 && (
                <>
                  <p>No Declined found.</p> <hr />
                </>
              )
            )}
            <h4>Declined</h4>
            <div className='actors-list'>
              {invitations.map((invitation) => (
                <div className='actor-card-layout' key={invitation.id}>
                  <ActorCard
                    actor={{
                      user: invitation.invitedUser,
                      profile: invitation.invitedUser.profile || null,
                    }}
                    ratings={invitation.invitedUser.rate || 0}
                    userRole={invitation.invitedUser.role}
                    role={invitation.castingRole}
                    isFromSubmission={true}
                  />
                  <div className='invite-btn-group'>
                    <Tooltip title='Move back to previous status'>
                      <IconButton
                        onClick={() => RevertInvitationStatus(invitation)}
                      >
                        <FontAwesomeIcon
                          className='Icon-buttons'
                          icon={faUndo}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Message'>
                      <IconButton
                        onClick={() => handleMessage(invitation.invitedUser)}
                      >
                        <FontAwesomeIcon
                          className='Icon-buttons'
                          icon={faMessage}
                        />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
              {applications.map((application) => (
                <div className='actor-card-layout' key={application.id}>
                  <ActorCard
                    actor={{
                      user: application.invitedUser,
                      profile: application.invitedUser.profile || null,
                    }}
                    ratings={application.invitedUser.rate || 0}
                    userRole={application.invitedUser.role}
                    role={application.castingRole}
                    isFromSubmission={true}
                  />
                  <div className='invite-btn-group'>
                    <Tooltip title='Move back to Applied'>
                      <IconButton
                        onClick={() =>
                          ChangeApplicationStatus(application.id, 'pending')
                        }
                      >
                        <FontAwesomeIcon
                          className='Icon-buttons'
                          icon={faUndo}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Message'>
                      <IconButton
                        onClick={() => handleMessage(application.invitedUser)}
                      >
                        <FontAwesomeIcon
                          className='Icon-buttons'
                          icon={faMessage}
                        />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <Alert
        openSnackbar={openSnackbar}
        alertSeverity={alertSeverity}
        setOpenSnackbar={setOpenSnackbar}
        alertMessage={alertMessage}
      />
    </div>
    // <div className="submission-container">
    //   <div className="content-header">
    //     <h1>Casting Call Title: {castingTitle}</h1>
    //   </div>
    //   <div className="status-tabs">
    //     {["Invited", "Applied", "Shortlist", "Declined"].map((tab) => (
    //       <span
    //         key={tab}
    //         className={`tab ${activeTab === tab ? "active" : ""}`}
    //         onClick={() => setActiveTab(tab)}
    //       >
    //         {tab}
    //       </span>
    //     ))}
    //   </div>

    //   <div className="content">
    //     {loading ? <p>Loading...</p> : invitations.length === 0 ? <p>No submissions found.</p> : null}

    //     {/* invitation list */}
    //     <div className="sub-content">
    //       <h4>Invitations</h4>
    //       <div className="actors-list">
    //         {invitations.map((invitation) => (
    //           <div className="actor-card-layout">
    //               <ActorCard
    //               key={invitation.invitedUser.id}
    //               actor={{
    //                 user: invitation.invitedUser,
    //                 profile: invitation.invitedUser.profile || null,
    //               }}
    //               ratings={invitation.invitedUser.rate || 0}
    //               userRole={invitation.invitedUser.role}
    //               role={invitation.castingRole}
    //               />
    //               <div className="invite-btn-group">
    //                 {invitation.inviteStatus === "reject" ? (
    //                   <Tooltip title="Already Rejected">
    //                     <StyledBadge badgeContent="Rejected">
    //                       <IconButton>
    //                         <FontAwesomeIcon className="Icon-buttons" icon={faBan} size="lg" />
    //                       </IconButton>
    //                     </StyledBadge>
    //                   </Tooltip>
    //                 ) : (
    //                   <Tooltip title="Decline">
    //                     <IconButton  onClick={() => ChangeInvitationStatus(invitation.id, 'reject')}>
    //                       <FontAwesomeIcon className="Icon-buttons" icon={faBan} />
    //                     </IconButton>
    //                   </Tooltip>
    //                 )}
    //                 <Tooltip title="Shortlist">
    //                   <IconButton  onClick={()=>ChangeInvitationStatus(invitation.id,'shortlist')} >
    //                   <FontAwesomeIcon className="Icon-buttons" icon={faTasks} />
    //                   </IconButton>
    //                 </Tooltip>
    //               </div>

    //             </div>
    //         ))}
    //       </div>
    //     </div>

    //     {(activeTab === "Applied" || activeTab === "Declined" || activeTab === "Shortlist") && (
    //       <>
    //         <hr />
    //         <div className="sub-content">
    //           <h4>Direct Applications</h4>
    //           <div className="actors-list">
    //             {/* direct applied list */}
    //             {applications.map((application) => (
    //               <div className="actor-card-layout">
    //                 <ActorCard
    //                 key={application.invitedUser.id}
    //                 actor={{
    //                   user: application.invitedUser,
    //                   profile: application.invitedUser.profile || null,
    //                 }}
    //                 ratings={application.invitedUser.rate || 0}
    //                 userRole={application.invitedUser.role}
    //                 role={application.castingRole}
// _                   />
    //               <div className="invite-btn-group">
    //                 <Tooltip title="Reject">
    //                   <IconButton onClick={()=>ChangeApplicationStatus(application.id,'reject')}>
    //                   <FontAwesomeIcon icon={faBan} />
    //                   </IconButton>
    //                 </Tooltip>
    //                 <Tooltip title="Shortlist">
    //                   <IconButton color="success" onClick={()=>ChangeApplicationStatus(application.id,'shortlist')} >
    //                   <FontAwesomeIcon icon={faTasks} />
    //                   </IconButton>
    //                 </Tooltip>
    //               </div>
    //               </div>
    //             ))}
    //           </div>
    //         </div>
    //       </>
    //     )}
    //   </div>
    //   <Alert
    //     openSnackbar={openSnackbar}
    //     alertSeverity={alertSeverity}
    //     setOpenSnackbar={setOpenSnackbar}
    //     alertMessage={alertMessage}
    //   />
    // </div>
  );
};

export default Submission;
